import { Activity, useEffect, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'

import DeviceIcon from '@mui/icons-material/Devices'
import PersonIcon from '@mui/icons-material/Person'
import { capitalize } from '../../utilities/stringUtils'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
  </Box>
)

const UserPage = () => {
  const [user, setUser] = useState(null)
  const [assignedDevices, setAssignedDevices] = useState([])
  const [availableDevices, setAvailableDevices] = useState([])
  const [selectedDevices, setSelectedDevices] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const { uuid } = useParams()
  const navigate = useNavigate()
  const { setBreadcrumbs } = usePageTitle()

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await fetchWithAuth(`users/${uuid}`)
        setUser(data)
        setBreadcrumbs([
          'users',
          `${capitalize(data.profile?.firstName)} ${capitalize(data.profile?.lastName)}`,
        ])
      } catch (error) {
        console.error('Error fetching user:', error)
      }
    }

    const fetchAssignedDevices = async () => {
      try {
        const { devices } = await fetchWithAuth(`users/${uuid}/devices`)
        setAssignedDevices(devices || [])
        setSelectedDevices((devices || []).map((device) => device.uuid))
      } catch (error) {
        console.error('Error fetching assigned devices:', error)
      }
    }

    fetchUser()
    fetchAssignedDevices()

    return () => {
      setBreadcrumbs([])
    }
  }, [uuid, setBreadcrumbs])

  const fetchOrganizationDevices = async () => {
    if (!user?.organization?.uuid) return

    try {
      const response = await fetchWithAuth(
        `devices?organization=${user.organization.uuid}`,
      )
      setAvailableDevices(response.data.data || [])
    } catch (error) {
      console.error('Error fetching organization devices:', error)
    }
  }

  const handleOpenDialog = async () => {
    await fetchOrganizationDevices()
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }

  const handleToggleDevice = (deviceUuid) => {
    setSelectedDevices((prev) => {
      if (prev.includes(deviceUuid)) {
        return prev.filter((id) => id !== deviceUuid)
      } else {
        return [...prev, deviceUuid]
      }
    })
  }

  const handleSaveDeviceAssignments = async () => {
    try {
      await fetchWithAuth(`users/${uuid}/devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceUuids: selectedDevices }),
      })

      const { devices } = await fetchWithAuth(`users/${uuid}/devices`)
      setAssignedDevices(devices || [])
      setDialogOpen(false)
    } catch (error) {
      console.error('Error saving device assignments:', error)
    }
  }

  const handleDeleteUser = async () => {
    try {
      const { status } = await fetchWithAuth(`users/${uuid}`, {
        method: 'DELETE',
      })

      if (status === 200) {
        navigate('/users')
      } else {
        console.error('Error deleting user:', status)
      }
    } catch (error) {
      console.error('Error deleting user:', error)
    }
    setDeleteDialogOpen(false)
  }

  if (!user) {
    return <Typography>Loading...</Typography>
  }

  return (
    <Grid container spacing={3} size={12}>
      <Grid size={12}>
        <Card>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                <PersonIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Box flex={1}>
                <Typography variant="h5" gutterBottom>
                  {capitalize(user.profile.firstName)}{' '}
                  {capitalize(user.profile.lastName)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {user.email}
                </Typography>
                <Box mt={1}>
                  <Activity mode={user.isDeleted ? 'visible' : 'hidden'}>
                    <Chip
                      label="Deleted"
                      color="error"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  </Activity>

                  <Chip label={user.role} color="primary" size="small" />
                </Box>
              </Box>
              {!user.isDeleted && (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(`/users/${uuid}/edit`)}
                  >
                    Edit User
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete User
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Organization Details
            </Typography>
            <InfoItem
              label="Organization"
              value={user.organization?.name || 'N/A'}
            />
            <InfoItem
              label="Registration Date"
              value={new Date(user.createdAt).toLocaleDateString()}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              User Settings
            </Typography>
            <InfoItem
              label="Date Format"
              value={user.settings?.dateFormat || 'N/A'}
            />
            <InfoItem
              label="Timezone"
              value={user.settings?.timezone || 'N/A'}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={12}>
        <Card>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" color="primary">
                Device Access
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenDialog}
              >
                Manage Devices
              </Button>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {assignedDevices.length > 0 ? (
                assignedDevices.map((device) => (
                  <Chip
                    key={device.uuid}
                    icon={<DeviceIcon />}
                    label={device.name}
                    color="primary"
                  />
                ))
              ) : (
                <Typography color="text.secondary">
                  No specific devices assigned. User has access to all
                  organization devices.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Device Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Select devices this user can access. If no devices are selected, the
            user will have access to all organization devices.
          </Typography>

          <List>
            {availableDevices.map((device) => (
              <ListItem
                key={device.uuid}
                button
                onClick={() => handleToggleDevice(device.uuid)}
              >
                <Checkbox
                  edge="start"
                  checked={selectedDevices.includes(device.uuid)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary={device.name} />
              </ListItem>
            ))}
            <Activity
              mode={availableDevices.length === 0 ? 'visible' : 'hidden'}
            >
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No devices available in this organization.
              </Typography>
            </Activity>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveDeviceAssignments} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete User?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

export default UserPage
